/**
 * PaymentChoicePrompt (08/2026) — Einmaliger Onboarding-Dialog im Kundenportal:
 * "Wie möchtest du bezahlen?" Selbstzahler (Rechnung + Twint/Payrexx-Link)
 * oder automatische Belastung pro Training. Erscheint nur solange
 * payment_preference noch nicht gesetzt ist (z.B. nach OTP-Erstanmeldung).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentChoicePrompt = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let shown = false;
    const check = async () => {
      const token = localStorage.getItem('customerToken');
      if (!token) return;
      if (sessionStorage.getItem('paymentChoiceAsked')) return;
      try {
        const res = await axios.get(`${API}/portal/payment-preference`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.payment_preference || res.data?.auto_charge_card) return;
      } catch (e) {
        return; // still/leise — kein Blocker
      }
      // Warten bis kein anderer Dialog (z.B. Willkommens-Tour) mehr offen ist
      const tryShow = () => {
        if (cancelled || shown) return;
        const otherDialog = document.querySelector('[role="dialog"], [data-tour-overlay]');
        if (!otherDialog) {
          shown = true;
          setVisible(true);
        } else {
          setTimeout(tryShow, 2000);
        }
      };
      setTimeout(tryShow, 1500);
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const choose = async (autoCharge) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('customerToken');
      await axios.put(
        `${API}/portal/payment-preference`,
        { auto_charge: autoCharge },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      sessionStorage.setItem('paymentChoiceAsked', '1');
      toast.success(autoCharge
        ? t('portal.payment.toast_auto')
        : t('portal.payment.toast_self'));
      setVisible(false);
    } catch (e) {
      toast.error(t('portal.payment.toast_error'));
      setVisible(false);
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }} data-testid="payment-choice-prompt">
      <div className="w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: '#141310', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 className="text-lg font-bold text-white mb-1">{t('portal.payment.title')}</h3>
        <p className="text-sm text-white/50 mb-4">{t('portal.payment.subtitle')}</p>
        <div className="space-y-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => choose(false)}
            data-testid="payment-choice-self"
            className="w-full text-left p-4 rounded-xl border border-white/10 bg-white/5 hover:border-yellow-500/60 transition-colors"
          >
            <div className="text-sm font-semibold text-white">{t('portal.payment.self_title')}</div>
            <div className="text-xs text-white/50 mt-0.5">{t('portal.payment.self_desc')}</div>
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => choose(true)}
            data-testid="payment-choice-auto"
            className="w-full text-left p-4 rounded-xl border border-white/10 bg-white/5 hover:border-yellow-500/60 transition-colors"
          >
            <div className="text-sm font-semibold text-white">{t('portal.payment.auto_title')}</div>
            <div className="text-xs text-white/50 mt-0.5">{t('portal.payment.auto_desc')}</div>
          </button>
        </div>
        <button
          type="button"
          onClick={() => { sessionStorage.setItem('paymentChoiceAsked', '1'); setVisible(false); }}
          className="w-full mt-3 text-xs text-white/40 hover:text-white/70 transition-colors"
          data-testid="payment-choice-later"
        >
          {t('portal.payment.later')}
        </button>
      </div>
    </div>
  );
};

export default PaymentChoicePrompt;
